import { useState, useRef, useCallback } from "react";
import { useDragAndDrop } from "../useDragAndDrop";
import { handleFileUpload } from "../../utils/fileUploadHandler";
import spaceStore from "../../store/spaceStore";

/**
 * Manages file handling logic including drag & drop, file input, and visual feedback.
 * Consolidates all file-related UI state and event handlers.
 * 
 * @param {string} chat_id - Current chat ID
 * @param {string|undefined} space_id - Spectra space ID (if in spectra context)
 * @param {object} user - User object with uid
 * @param {string} orgId - Organization ID
 * @param {array} files - Current files array from FileStore
 * @param {function} setFiles - FileStore setter for files
 * @param {number} messageCount - Current message count
 * @param {boolean} isNewChat - Whether this is a new unsaved chat
 * @returns {object} File handling state and handlers
 */
export const useFileHandling = (
  chat_id,
  space_id,
  user,
  orgId,
  files,
  setFiles,
  messageCount,
  isNewChat
) => {
  const [isNewDrag, setIsNewDrag] = useState(false);
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);
  const { handleDrop } = useDragAndDrop();
  
  /**
   * Handle drag enter - show drop overlay
   */
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    if (e.dataTransfer?.types?.includes("Files")) {
      setIsNewDrag(true);
    }
  }, []);
  
  /**
   * Handle drag over - maintain drop overlay
   */
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    if (e.dataTransfer?.types?.includes("Files")) {
      setIsNewDrag(true);
    }
  }, []);
  
  /**
   * Handle drag leave - hide drop overlay when leaving container
   */
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    if (
      containerRef.current &&
      !containerRef.current.contains(e.relatedTarget)
    ) {
      setIsNewDrag(false);
    }
  }, []);
  
  /**
   * Handle file input change event
   */
  const handleOnChange = useCallback((e) => {
    const space = spaceStore
      .getState()
      .spaces?.find((s) => s?.space_id === space_id);

    handleFileUpload(
      e,
      chat_id,
      fileInputRef,
      setFiles,
      files,
      user,
      orgId,
      messageCount,
      isNewChat,
      space
    );
  }, [chat_id, space_id, fileInputRef, setFiles, files, user, orgId, messageCount, isNewChat]);
  
  /**
   * Handle file drop event
   */
  const handleOnDrop = useCallback((e) => {
    setIsNewDrag(false);
    const space = spaceStore
      .getState()
      .spaces?.find((s) => s?.space_id === space_id);

    handleDrop(
      e,
      fileInputRef,
      handleFileUpload,
      chat_id,
      setFiles,
      files,
      user,
      orgId,
      space,
      messageCount
    );
    
    // Focus prompt textarea after drop
    const promptTextarea = document.getElementById("prompt-textarea");
    if (promptTextarea) {
      promptTextarea.focus();
    }
  }, [space_id, handleDrop, chat_id, setFiles, files, user, orgId, messageCount]);
  
  return {
    isNewDrag,
    setIsNewDrag,
    fileInputRef,
    containerRef,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleOnChange,
    handleOnDrop,
  };
};
